// API Konfiguration
const API_CONFIG = {
  baseUrl: "https://api.abaninja.ch",
  accountUuid: "",
  bearerToken: "",
};

// Konfiguration setzen
export const configure = (config) => {
  API_CONFIG.accountUuid = config.accountUuid;
  API_CONFIG.bearerToken = config.bearerToken;
};

// Basis Request Funktion
export const request = async (endpoint, options = {}) => {
  const url = `${API_CONFIG.baseUrl}${endpoint}`;
  const headers = {
    Authorization: `Bearer ${API_CONFIG.bearerToken}`,
    "Content-Type": "application/json",
    ...options.headers,
  };

  try {
    const response = await fetch(url, {
      ...options,
      headers,
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error("API Error Response:", errorText);
      throw new Error(
        `API Error: ${response.status} - ${response.statusText} - ${errorText}`
      );
    }

    // Prüfe ob Response Content hat
    const contentLength = response.headers.get("content-length");
    const contentType = response.headers.get("content-type");

    if (contentLength === "0" || !contentType?.includes("application/json")) {
      return {}; // Leeres Objekt für erfolgreiche Requests ohne JSON Content
    }

    const text = await response.text();
    if (!text) {
      return {}; // Leeres Objekt für leere Responses
    }

    return JSON.parse(text);
  } catch (error) {
    console.error("API Request failed:", error);
    throw error;
  }
};

// Alle Aufträge laden (für Kalender-Anzeige)
export const getContracts = async () => {
  const data = await request(
    `/accounts/${API_CONFIG.accountUuid}/documents/v2/contract-notes`
  );

  return data.data.documents
    .filter((doc) => {
      return (
        !doc.isArchived &&
        !doc.isCancelled &&
        (doc.sentStatus === "SENT" ||
          doc.sentStatus === "OPEN" ||
          doc.status === "converted")
      );
    })
    .map((doc) => {
      return {
        uuid: doc.uuid,
        title:
          doc.title ||
          doc.reference ||
          `${doc.reference} - ${doc.receiver?.name || "Unbekannt"}`,
        number: doc.contractNoteNumber,
        customer: doc.receiver?.name || "Unbekannt",
        addressUuid: doc.receiver?.addressUuid,
        companyUuid: doc.receiver?.companyUuid,
        // Zusätzliche Felder für bessere Darstellung
        displayTitle: doc.title || doc.reference || "Ohne Titel",
        reference: doc.reference || doc.title || doc.contractNoteNumber, // VOLLSTÄNDIGE Reference
        fullReference: doc.title
          ? `${doc.reference} - ${doc.title}`
          : doc.reference
          ? `${doc.reference} - ${doc.reference}`
          : doc.contractNoteNumber,
        // Status-Informationen für Filterung
        sentStatus: doc.sentStatus,
        status: doc.status,
        isOpen: doc.sentStatus === "OPEN" || doc.status === "pending",
        documentTotal: doc.documentTotal,
        receiver: doc.receiver,
        // Für Projektübersicht: Gekürzte Version
        projectReference: doc.reference ? doc.reference.substring(0, 3) : "",
        // Original-Daten für Debug
        originalData: {
          sentStatus: doc.sentStatus,
          status: doc.status,
          isArchived: doc.isArchived,
          isCancelled: doc.isCancelled,
          receiver: doc.receiver,
        },
      };
    });
};

export const getAllContractsForProjects = async () => {
  const data = await request(
    `/accounts/${API_CONFIG.accountUuid}/documents/v2/contract-notes`
  );
  return data.data.documents.map((doc) => ({
    uuid: doc.uuid,
    title: doc.title || "",
    reference: doc.reference.substring(0, 3) || "",
    number: doc.contractNoteNumber,
    customer: doc.receiver?.name || "Unbekannt",
    addressUuid: doc.receiver?.addressUuid,
    companyUuid: doc.receiver?.companyUuid,
    sentStatus: doc.sentStatus,
    status: doc.status,
    isArchived: doc.isArchived || false,
    isCancelled: doc.isCancelled || false,
    documentTotal: doc.documentTotal || 0,
    receiver: doc.receiver,
    contractNoteNumber: doc.contractNoteNumber,
  }));
};

// Nur offene Aufträge für Formular-Auswahl
export const getOpenContracts = async () => {
  const allContracts = await getContracts();
  const openContracts = allContracts.filter((contract) => contract.isOpen);
  return openContracts;
};

// Mitarbeiter:in laden
export const getEmployees = async () => {
  const data = await request(
    `/accounts/${API_CONFIG.accountUuid}/time/v2/employees`
  );
  return data.data;
};

// Aktivitätstypen laden
export const getActivityTypes = async () => {
  const data = await request(
    `/accounts/${API_CONFIG.accountUuid}/time/v2/settings/activity-types`
  );
  return data.data;
};

export const getInvoices = async () => {
  const data = await request(
    `/accounts/${API_CONFIG.accountUuid}/documents/v2/invoices`
  );
  return data.data.documents.filter(
    (doc) => !doc.isArchived && !doc.isCancelled && doc.sentStatus
  );
};

// Zeiteinträge für eine Mitarbeiter:in in einem Zeitraum laden
export const getTimeEntries = async (employeeUuid, startDate, endDate) => {
  try {
    // Erstelle Array aller Tage im Zeitraum
    const start = new Date(startDate);
    const end = new Date(endDate);
    const dates = [];

    for (
      let date = new Date(start);
      date <= end;
      date.setDate(date.getDate() + 1)
    ) {
      dates.push(date.toISOString().split("T")[0]);
    }

    // Parallel alle Tage abfragen
    const promises = dates.map(async (date) => {
      try {
        const endpoint = `/accounts/${API_CONFIG.accountUuid}/time/v2/employees/${employeeUuid}/daily/${date}`;

        const data = await request(endpoint);

        // Verarbeite die Antwort - die Struktur kann variieren
        const activities = data.data || data.activities || data;

        if (Array.isArray(activities)) {
          // Filtere und lade Details für jede Activity
          const filteredActivities = activities.filter((activity) => {
            // Filtere leere Container ohne UUID raus
            if (!activity.uuid) {
              return false;
            }

            // Filtere Pausen-Aktivitäten raus
            const typeName = activity.typeName?.toLowerCase() || "";
            const isPause =
              typeName.includes("pause") || typeName.includes("break");

            return !isPause;
          });

          // NEUE LOGIK: Lade Details für jede Activity
          const detailPromises = filteredActivities.map(async (activity) => {
            try {
              const detailEndpoint = `/accounts/${API_CONFIG.accountUuid}/time/v2/employees/${employeeUuid}/activities/${activity.uuid}`;

              const detailData = await request(detailEndpoint);
              const activityDetail = detailData.data || detailData;

              return {
                uuid: activity.uuid,
                date: date,
                startTime: activity.startTime || "08:00",
                endTime: activity.endTime || "18:00",
                duration: activity.duration,
                description:
                  activityDetail.description || activity.comment || "", // Bevorzuge description aus Details
                contractUuid: activity.contractNoteUuid || "",
                contractTitle: activity.contractNoteName || "",
                activityTypeUuid: activity.typeId,
                typeName: activity.typeName || "",
                employeeUuid: employeeUuid,
                isFullDay: activity.fullDay || false,
                isPause: false,
              };
            } catch (detailError) {
              console.warn(
                `Fehler beim Laden der Activity Details für ${activity.uuid}:`,
                detailError
              );
              // Fallback: Verwende die Basis-Daten
              return {
                uuid: activity.uuid,
                date: date,
                startTime: activity.startTime || "08:00",
                endTime: activity.endTime || "18:00",
                duration: activity.duration,
                description: activity.comment || "",
                contractUuid: activity.contractNoteUuid || "",
                contractTitle: activity.contractNoteName || "",
                activityTypeUuid: activity.typeId,
                typeName: activity.typeName || "",
                employeeUuid: employeeUuid,
                isFullDay: activity.fullDay || false,
                isPause: false,
              };
            }
          });

          return await Promise.all(detailPromises);
        } else {
          // Keine Aktivitäten für diesen Tag
          return [];
        }
      } catch (error) {
        return []; // Leeres Array wenn keine Daten für diesen Tag
      }
    });

    // Warte auf alle Requests und flache das Ergebnis ab
    const results = await Promise.all(promises);
    const allEntries = results.flat();

    return allEntries;
  } catch (error) {
    return [];
  }
};

export const getProducts = async () => {
  try {
    const data = await request(
      `/accounts/${API_CONFIG.accountUuid}/products/v2/products`
    );
    return data.data
      .filter((product) => {
        // Filtere nur Services (Dienstleistungen) und nicht archivierte
        return product.isService && !product.archivedAt;
      })
      .map((product) => {
        // Bestimme den Anzeigenamen (bevorzuge deutsche, dann englische Übersetzung)
        const germanName = product.translations?.de?.productName;
        const englishName = product.translations?.en?.productName;
        const productName =
          germanName && germanName.trim()
            ? germanName
            : englishName && englishName.trim()
            ? englishName
            : "Keine Beschreibung";

        const germanDesc = product.translations?.de?.productDescription;
        const englishDesc = product.translations?.en?.productDescription;
        const productDesc =
          germanDesc && germanDesc.trim()
            ? germanDesc
            : englishDesc && englishDesc.trim()
            ? englishDesc
            : "";

        return {
          uuid: product.productUuid, // Korrigiert: productUuid statt uuid
          productKey: product.productKey,
          isService: product.isService,
          archivedAt: product.archivedAt,
          cost: parseFloat(product.cost) || 0,
          taxRate: product.taxRate,
          // Deutsche Übersetzungen
          translations: product.translations,
          // Zusätzliche Felder für bessere Darstellung
          displayName: productName,
          description: productDesc,
          fullDisplayName: `${product.productKey} - ${productName}`,
          // Unit-Information
          unit:
            product.productUnit?.translations?.de?.unit ||
            product.productUnit?.isocode ||
            "",
          // Original-Daten für Debug
          originalData: product,
        };
      });
  } catch (error) {
    return []; // Fallback: Leeres Array zurückgeben
  }
};

export const createTimeEntry = async (entry) => {
  const employeeUuid = entry.employeeUuid;
  const endpoint = `/accounts/${API_CONFIG.accountUuid}/time/v2/employees/${employeeUuid}/activities`;

  // Für Day Activities: Verwende isFullDay = true und startDate/endDate
  if (entry.isFullDay) {
    const payload = {
      activityTypeUuid: entry.activityTypeUuid,
      startDate: entry.startDate,
      endDate: entry.endDate,
      startTime: "08:00",
      endTime: "18:00",
      halfDay: false,
      description: entry.description || null,
    };

    // Auftrag nur bei "Arbeit" erforderlich
    if (entry.contractUuid && entry.addressUuid) {
      payload.contractNoteUuid = entry.contractUuid;
      payload.addressUuid = entry.addressUuid;
    } else {
      payload.contractNoteUuid = null;
      payload.addressUuid = null;
    }

    // Produkt optional
    if (entry.productUuid) {
      payload.productUuid = entry.productUuid;
    } else {
      payload.productUuid = null;
    }

    return await request(endpoint, {
      method: "POST",
      body: JSON.stringify(payload),
    });
  }

  // Normale Zeiteinträge (bestehender Code)
  const payload = {
    activityTypeUuid: entry.activityTypeUuid,
    startDate: entry.isFullDay ? entry.startDate : entry.date,
    endDate: entry.isFullDay ? entry.endDate : entry.date,
    startTime: entry.isFullDay ? "08:00" : entry.startTime,
    endTime: entry.isFullDay ? "18:00" : entry.endTime,
    halfDay: false,
    description: entry.description || null,
  };

  // Auftrag nur bei "Arbeit" erforderlich
  if (entry.contractUuid && entry.addressUuid) {
    payload.contractNoteUuid = entry.contractUuid;
    payload.addressUuid = entry.addressUuid;
  } else {
    payload.contractNoteUuid = null;
    payload.addressUuid = null;
  }

  // Produkt optional
  if (entry.productUuid) {
    payload.productUuid = entry.productUuid;
  } else {
    payload.productUuid = null;
  }

  return await request(endpoint, {
    method: "POST",
    body: JSON.stringify(payload),
  });
};

// Zeiteintrag löschen (korrigiert für leere Responses)
export const deleteTimeEntry = async (employeeUuid, activityUuid) => {
  const endpoint = `/accounts/${API_CONFIG.accountUuid}/time/v2/employees/${employeeUuid}/activities/${activityUuid}`;

  try {
    const response = await request(endpoint, {
      method: "DELETE",
    });

    return response || {}; // Fallback für leere Response
  } catch (error) {
    throw error;
  }
};

// Zeiteintrag bearbeiten (optional für zukünftige Funktionalität)
export const updateTimeEntry = async (employeeUuid, activityUuid, entry) => {
  const endpoint = `/accounts/${API_CONFIG.accountUuid}/time/v2/employees/${employeeUuid}/activities/${activityUuid}`;

  const payload = {
    activityTypeUuid: entry.activityTypeUuid,
    startDate: entry.date,
    endDate: entry.date,
    startTime: entry.startTime,
    endTime: entry.endTime,
    halfDay: false,
    contractNoteUuid: entry.contractUuid,
    addressUuid: entry.addressUuid,
    description: entry.description,
  };

  return await request(endpoint, {
    method: "PUT",
    body: JSON.stringify(payload),
  });
};

// Verbindung testen
export const testConnection = async () => {
  try {
    await getEmployees();
    return { success: true, message: "Verbindung erfolgreich!" };
  } catch (error) {
    return {
      success: false,
      message: `Verbindung fehlgeschlagen: ${error.message}`,
    };
  }
};

// In apiService.js
export const getContractBookingCount = async (contractUuid) => {
  try {
    const data = await request(
      `/accounts/${API_CONFIG.accountUuid}/documents/v2/contract-notes/${contractUuid}`
    );
    
    const contract = data.data;
    
    // Zähle Positionen mit employeeActivityUuid (= Zeitbuchungen)
    const bookingCount = contract.documents[0].positions.length;

    return bookingCount;
  } catch (error) {
    console.error('Fehler beim Laden der Buchungsanzahl:', error);
    return 0;
  }
};